/**
 * Connection Pool Timeline Component
 *
 * Displays a line chart visualization of connection pool utilization over time.
 * Features:
 * - Line chart showing utilization trends
 * - Time-based filtering (1h, 6h, 24h, 7d)
 * - Color-coded zones (normal/warning/critical)
 * - Tooltips with detailed stats
 * - WCAG 2.1 Level AA accessible
 * - Uses Shadcn/ui components
 */

import * as React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle, TrendingUp, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useConnectionPoolHistory } from '../../../hooks/admin';
import type { ConnectionPoolHistoryPoint } from '../../../hooks/admin/types/database';
import { cn } from '@/lib/utils';

export interface ConnectionPoolTimelineProps {
  className?: string;
}

type TimeRange = '1' | '6' | '24' | '168';

/**
 * Simple line chart component using SVG
 */
interface LineChartProps {
  data: ConnectionPoolHistoryPoint[];
  width: number;
  height: number;
}

function LineChart({ data, width, height }: LineChartProps) {
  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center" style={{ width, height }}>
        <p className="text-sm text-muted-foreground">No data available</p>
      </div>
    );
  }

  const padding = { top: 20, right: 20, bottom: 40, left: 50 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  // Calculate scales
  const maxUtilization = Math.max(...data.map((d) => d.utilization_pct), 100);
  const yScale = (value: number) => chartHeight - (value / maxUtilization) * chartHeight;
  const xScale = (index: number) => (index / (data.length - 1)) * chartWidth;

  // Generate path
  const path = data
    .map((point, index) => {
      const x = xScale(index) + padding.left;
      const y = yScale(point.utilization_pct) + padding.top;
      return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    })
    .join(' ');

  // Generate area path (filled under the line)
  const areaPath =
    path +
    ` L ${xScale(data.length - 1) + padding.left} ${chartHeight + padding.top} L ${padding.left} ${chartHeight + padding.top} Z`;

  // Format time for x-axis labels
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  // Show labels for first, middle, and last points
  const labelIndices = [0, Math.floor(data.length / 2), data.length - 1];

  return (
    <svg width={width} height={height} className="overflow-visible">
      {/* Background zones */}
      <rect
        x={padding.left}
        y={padding.top}
        width={chartWidth}
        height={yScale(80) - yScale(100)}
        fill="hsl(var(--destructive))"
        opacity={0.1}
      />
      <rect
        x={padding.left}
        y={yScale(80) + padding.top}
        width={chartWidth}
        height={yScale(60) - yScale(80)}
        fill="hsl(var(--warning))"
        opacity={0.1}
      />

      {/* Grid lines */}
      {[0, 25, 50, 75, 100].map((value) => (
        <g key={value}>
          <line
            x1={padding.left}
            y1={yScale(value) + padding.top}
            x2={width - padding.right}
            y2={yScale(value) + padding.top}
            stroke="hsl(var(--border))"
            strokeWidth="1"
            strokeDasharray="2,2"
          />
          <text
            x={padding.left - 10}
            y={yScale(value) + padding.top + 4}
            textAnchor="end"
            fontSize="12"
            fill="hsl(var(--muted-foreground))"
          >
            {value}%
          </text>
        </g>
      ))}

      {/* Area under line */}
      <path d={areaPath} fill="hsl(var(--primary))" opacity={0.1} />

      {/* Line */}
      <path d={path} fill="none" stroke="hsl(var(--primary))" strokeWidth="2" />

      {/* Data points */}
      {data.map((point, index) => (
        <g key={index}>
          <circle
            cx={xScale(index) + padding.left}
            cy={yScale(point.utilization_pct) + padding.top}
            r="3"
            fill="hsl(var(--primary))"
            className="hover:r-5 transition-all cursor-pointer"
          >
            <title>
              {formatTime(point.recorded_at)}: {point.utilization_pct.toFixed(1)}%{'\n'}
              Active: {point.active}, Idle: {point.idle}, Total: {point.total}/{point.max}
            </title>
          </circle>
        </g>
      ))}

      {/* X-axis labels */}
      {labelIndices.map((index) => {
        if (index >= data.length) return null;
        return (
          <text
            key={index}
            x={xScale(index) + padding.left}
            y={height - padding.bottom + 20}
            textAnchor="middle"
            fontSize="12"
            fill="hsl(var(--muted-foreground))"
          >
            {formatTime(data[index].recorded_at)}
          </text>
        );
      })}

      {/* Y-axis label */}
      <text
        x={-height / 2}
        y={15}
        transform="rotate(-90)"
        textAnchor="middle"
        fontSize="12"
        fill="hsl(var(--muted-foreground))"
      >
        Utilization (%)
      </text>
    </svg>
  );
}

/**
 * ConnectionPoolTimelineSkeleton - Loading skeleton for the timeline
 */
function ConnectionPoolTimelineSkeleton() {
  return (
    <Card data-testid="connection-pool-timeline-skeleton">
      <CardHeader>
        <Skeleton className="h-6 w-64" />
        <Skeleton className="h-4 w-80" />
      </CardHeader>
      <CardContent className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </CardContent>
    </Card>
  );
}

/**
 * ConnectionPoolTimeline - Main component
 *
 * Displays a line chart of connection pool utilization over time with
 * configurable time ranges.
 */
export function ConnectionPoolTimeline({ className }: ConnectionPoolTimelineProps) {
  const [timeRange, setTimeRange] = React.useState<TimeRange>('24');
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const { data, isLoading, isError, mutate } = useConnectionPoolHistory({
    hours: parseInt(timeRange),
  });

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await mutate();
    setTimeout(() => setIsRefreshing(false), 500);
  };

  if (isLoading) {
    return <ConnectionPoolTimelineSkeleton />;
  }

  if (isError || !data) {
    return (
      <Card className={className} data-testid="connection-pool-timeline-error">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error Loading Timeline</AlertTitle>
          <AlertDescription>
            Failed to load connection pool history. Ensure the background job is collecting data.
          </AlertDescription>
        </Alert>
      </Card>
    );
  }

  const hasData = data.history.length > 0;

  // Calculate stats
  const avgUtilization = hasData
    ? data.history.reduce((sum, point) => sum + point.utilization_pct, 0) / data.history.length
    : 0;

  const maxUtilization = hasData
    ? Math.max(...data.history.map((point) => point.utilization_pct))
    : 0;

  const peakConnections = hasData
    ? Math.max(...data.history.map((point) => point.total))
    : 0;

  return (
    <Card className={className} data-testid="connection-pool-timeline">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" aria-hidden="true" />
            <CardTitle className="text-lg">Connection Pool Timeline</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefresh}
            disabled={isRefreshing}
            aria-label="Refresh connection pool timeline"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        <CardDescription>Historical connection pool utilization</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Time Range Tabs */}
        <div className="inline-flex rounded-md shadow-sm" role="group">
          {[
            { value: '1', label: '1 Hour' },
            { value: '6', label: '6 Hours' },
            { value: '24', label: '24 Hours' },
            { value: '168', label: '7 Days' },
          ].map((range, index, array) => (
            <Button
              key={range.value}
              type="button"
              onClick={() => setTimeRange(range.value as TimeRange)}
              variant={timeRange === range.value ? 'default' : 'outline'}
              className={cn(
                'px-4 py-2',
                index === 0 && 'rounded-r-none',
                index === array.length - 1 && 'rounded-l-none',
                index > 0 && index < array.length - 1 && 'rounded-none'
              )}
            >
              {range.label}
            </Button>
          ))}
        </div>

        {/* Chart */}
        {hasData ? (
          <div className="w-full overflow-x-auto">
            <LineChart data={data.history} width={800} height={300} />
          </div>
        ) : (
          <div className="flex items-center justify-center h-64 bg-muted/50 rounded-lg">
            <div className="text-center space-y-2">
              <AlertCircle className="h-8 w-8 text-muted-foreground mx-auto" />
              <p className="text-sm text-muted-foreground">
                No historical data available yet
              </p>
              <p className="text-xs text-muted-foreground">
                Data will appear once the background job starts collecting metrics
              </p>
            </div>
          </div>
        )}

        {/* Stats Summary */}
        {hasData && (
          <div className="grid grid-cols-3 gap-4 pt-4 border-t">
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground">Avg Utilization</span>
              <span className="text-lg font-semibold">{avgUtilization.toFixed(1)}%</span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground">Peak Utilization</span>
              <span className="text-lg font-semibold">{maxUtilization.toFixed(1)}%</span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-muted-foreground">Peak Connections</span>
              <span className="text-lg font-semibold">{peakConnections}</span>
            </div>
          </div>
        )}

        {/* Data count info */}
        <p className="text-xs text-muted-foreground">
          Showing {data.count} data point{data.count !== 1 ? 's' : ''} from the last {data.hours}{' '}
          hour{data.hours !== 1 ? 's' : ''}
        </p>
      </CardContent>
    </Card>
  );
}
